import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import { OffsetType, OffsetAnchor, CalendarView } from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { RawMultiCalendarSelect } from "components/form/legacyFields/multiCalendarSelect";
import { RawMultiNumericInput } from "components/form/legacyFields/multiNumericInput";
import { RawMultiSelectInput } from "components/form/legacyFields/multiSelectInput";
import { SelectInputOption } from "components/form/legacyFields/selectInput";
import {
    translateOffsetAnchor,
    translateOffsetType,
} from "org/control/list/utils";

import * as theme from "./theme.css";

const ALL_OFFSET_TYPES: Array<OffsetType> = ["BeforeAnchor", "AfterAnchor"];
const ALL_OFFSET_ANCHORS: Array<OffsetAnchor> = [
    "StartOfInterval",
    "EndOfInterval",
];

type PeriodType = "Month" | "TwoMonths" | "Quarter" | "HalfYear" | "Year";

interface MultiDefaultDueDateOffsetValues {
    months: Array<number>;
    days: Array<number>;
    calendarIds: Array<number | null>;
    offsetTypes: Array<OffsetType>;
    anchors: Array<OffsetAnchor>;
}

interface CommonProps {
    values: MultiDefaultDueDateOffsetValues;
    calendars: Array<CalendarView>;
    period: PeriodType;
    inline?: boolean;
    useDefaultCalendar?: boolean;
    textAfter?: string;
    nodeAfter?: React.ReactNode;
}

type ReadOnlyProps = CommonProps;

interface EditProps extends CommonProps {
    onChangeMonths: (months: number) => void;
    onChangeDays: (days: number) => void;
    onChangeCalendarId: (calendarId: number | null) => void;
    onChangeOffsetType: (offsetType: OffsetType) => void;
    onChangeAnchor: (anchor: OffsetAnchor) => void;
}

type MultiDefaultDueDateOffsetInputProps =
    | ({ type: "READ_ONLY" } & ReadOnlyProps)
    | ({ type: "EDIT" } & EditProps);

class MultiDefaultDueDateOffsetInput extends React.PureComponent<MultiDefaultDueDateOffsetInputProps> {
    render = () => {
        return (
            <BaseFormDisplay
                label={t("CreateControl_Recurrence_DateRule_Label")}
                className={
                    this.props.inline
                        ? theme.inlineMultiDefaultDueDateOffsetInput
                        : ""
                }
            >
                <div className={theme.multiDefaultDueDateOffsetInputContent}>
                    {this.renderMonthsInput()}
                    {t("CreateControl_Recurrence_DateRule_MonthsAnd")}
                    {this.renderDaysInputAndCalendarSelect()}
                    {this.renderOffsetTypeSelect()}
                    {t("CreateControl_Recurrence_DateRule_TheAnchor")}
                    {this.renderAnchorSelect()}
                    <span>
                        {translatePeriod(this.props.period)}{" "}
                        {this.props.textAfter}
                    </span>
                    {this.props.nodeAfter}
                </div>
            </BaseFormDisplay>
        );
    };

    private renderMonthsInput = () => {
        const { months } = this.props.values;

        switch (this.props.type) {
            case "EDIT": {
                const { onChangeMonths } = this.props;
                return (
                    <RawMultiNumericInput
                        type="EDIT"
                        name="months"
                        values={months}
                        min={0}
                        max={99}
                        onChange={fuse(onChangeMonths)}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiNumericInput
                        type="READ_ONLY"
                        name="months"
                        display="Input"
                        values={months}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };

    private renderDaysInputAndCalendarSelect = () => {
        // NOTE: +1 day adjustment is on purpose to correspond with the way data are stored in DB and displayed in Legacy
        const days = this.props.values.days.map((days) => days + 1);
        switch (this.props.type) {
            case "EDIT": {
                const { onChangeDays } = this.props;
                const onChange = (days: number | undefined) => {
                    // NOTE: -1 day adjustment is on purpose to correspond with the way data are stored in DB and displayed in Legacy
                    if (days !== undefined) {
                        onChangeDays(days - 1);
                    }
                };

                return (
                    <RawMultiNumericInput
                        type="EDIT"
                        name="days"
                        values={days}
                        min={1}
                        max={31}
                        className={
                            theme.multiDefaultDueDateOffsetInputDaysInput
                        }
                        addonAfter={this.renderCalendarSelect()}
                        onChange={onChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiNumericInput
                        type="READ_ONLY"
                        name="days"
                        display="Input"
                        values={days}
                        className={
                            theme.multiDefaultDueDateOffsetInputDaysInput
                        }
                        addonAfter={this.renderCalendarSelect()}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };

    private renderCalendarSelect = () => {
        const { calendarIds } = this.props.values;
        const { calendars, useDefaultCalendar } = this.props;

        if (useDefaultCalendar) {
            return <span>{t("ReviewerOffset_Days")}</span>;
        }

        switch (this.props.type) {
            case "EDIT": {
                const { onChangeCalendarId } = this.props;
                return (
                    <RawMultiCalendarSelect
                        type="EDIT"
                        name="calendarId"
                        calendars={calendars}
                        values={calendarIds}
                        dropdownMatchSelectWidth={false}
                        className={
                            theme.multiDefaultDueDateOffsetInputCalendarSelect
                        }
                        onChange={onChangeCalendarId}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiCalendarSelect
                        type="READ_ONLY"
                        name="calendarId"
                        calendars={calendars}
                        values={calendarIds}
                        className={
                            theme.multiDefaultDueDateOffsetInputCalendarSelect
                        }
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };

    private renderOffsetTypeSelect = () => {
        const { props } = this;
        const { offsetTypes } = props.values;

        const options: Array<SelectInputOption> = ALL_OFFSET_TYPES.map(
            (value) => {
                return { value, label: translateOffsetType(value) };
            }
        );

        switch (props.type) {
            case "EDIT": {
                const { onChangeOffsetType } = props;
                const onChange = fuse((value: string) => {
                    onChangeOffsetType(stringToOffsetType(value));
                });

                return (
                    <RawMultiSelectInput
                        type="EDIT"
                        name="offsetType"
                        options={options}
                        values={offsetTypes}
                        onChange={onChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiSelectInput
                        type="READ_ONLY"
                        name="offsetType"
                        options={options}
                        values={offsetTypes}
                        display="Input"
                    />
                );
            }
            default:
                return assertNever(props);
        }
    };

    private renderAnchorSelect = () => {
        const { props } = this;
        const { anchors } = props.values;

        const options: Array<SelectInputOption> = ALL_OFFSET_ANCHORS.map(
            (value) => {
                return { value, label: translateOffsetAnchor(value) };
            }
        );

        switch (props.type) {
            case "EDIT": {
                const { onChangeAnchor } = props;
                const onChange = fuse((value: string) => {
                    onChangeAnchor(stringToAnchor(value));
                });

                return (
                    <RawMultiSelectInput
                        type="EDIT"
                        name="anchor"
                        options={options}
                        values={anchors}
                        onChange={onChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiSelectInput
                        type="READ_ONLY"
                        name="anchor"
                        options={options}
                        values={anchors}
                        display="Input"
                    />
                );
            }
            default:
                return assertNever(props);
        }
    };
}

function stringToOffsetType(value: string): OffsetType {
    const offsetType = value as OffsetType;
    if (ALL_OFFSET_TYPES.includes(offsetType)) {
        return offsetType;
    } else {
        throw new Error(`Cannot convert string "${value}" into OffsetType`);
    }
}

function stringToAnchor(value: string): OffsetAnchor {
    const anchor = value as OffsetAnchor;
    if (ALL_OFFSET_ANCHORS.includes(anchor)) {
        return anchor;
    } else {
        throw new Error(`Cannot convert string "${value}" into OffsetAnchor`);
    }
}

function fuse<T>(onChange: (arg: T) => void): (arg: T | undefined) => void {
    return (arg: T | undefined) => {
        if (arg !== undefined) {
            onChange(arg);
        }
    };
}

function translatePeriod(period: PeriodType): string {
    switch (period) {
        case "Month":
            return t("CreateControl_Recurrence_DateRule_OfTheMonth");
        case "TwoMonths":
            return t("CreateControl_Recurrence_DateRule_OfTheTwoMonth");
        case "Quarter":
            return t("CreateControl_Recurrence_DateRule_OfTheQuarter");
        case "HalfYear":
            return t("CreateControl_Recurrence_DateRule_OfTheHalf");
        case "Year":
            return t("CreateControl_Recurrence_DateRule_OfTheYear");
        default:
            return assertNever(period);
    }
}

export { MultiDefaultDueDateOffsetInput };
export type { PeriodType, MultiDefaultDueDateOffsetValues };
